import { lazy, type ComponentType } from 'react';
import { createBrowserRouter, useParams } from 'react-router';
import { CrashPage, NotFoundPage } from './components/NotFoundPage';
import { HomePage } from './features/home/HomePage';

/** A page loaded on its first visit, which `preload()` can also fetch ahead of time. */
function lazyPage<M>(load: () => Promise<M>, pick: (module: M) => ComponentType) {
  return Object.assign(
    lazy(() => load().then((module) => ({ default: pick(module) }))),
    { preload: load },
  );
}

// Only the home page ships in the first load; game screens bring the map engine with them.
const SoloSetupPage = lazyPage(() => import('./features/solo/SoloSetupPage'), (m) => m.SoloSetupPage);
const SoloGamePage = lazyPage(() => import('./features/solo/SoloGamePage'), (m) => m.SoloGamePage);
const PartyPage = lazyPage(() => import('./features/party/PartyPage'), (m) => m.PartyPage);
const TvPage = lazyPage(() => import('./features/party/TvPage'), (m) => m.TvPage);
const AccessPage = lazyPage(() => import('./features/access/AccessPage'), (m) => m.AccessPage);
const ChallengePage = lazyPage(() => import('./features/challenge/ChallengePage'), (m) => m.ChallengePage);
const DailyPage = lazyPage(() => import('./features/challenge/ChallengePage'), (m) => () => <m.ChallengePage daily />);

/** Fetches the game screens once the browser is idle, so starting a game doesn't wait. */
export function preloadGameScreens(): void {
  const preload = () => [SoloSetupPage, SoloGamePage, PartyPage].forEach((page) => void page.preload());
  if ('requestIdleCallback' in window) requestIdleCallback(preload, { timeout: 5000 });
  else setTimeout(preload, 2000);
}

/** Remount pages when moving from one game (or party) to the next. */
function SoloGameRoute() {
  const { gameId } = useParams();
  return <SoloGamePage key={gameId} />;
}

function PartyRoute() {
  const { code } = useParams();
  return <PartyPage key={code?.toUpperCase()} />;
}

export const router = createBrowserRouter([
  {
    errorElement: <CrashPage />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/solo', element: <SoloSetupPage /> },
      { path: '/access', element: <AccessPage /> },
      { path: '/challenge/:id', element: <ChallengePage /> },
      { path: '/daily', element: <DailyPage /> },
      { path: '/solo/:gameId', element: <SoloGameRoute /> },
      { path: '/party/:code', element: <PartyRoute /> },
      { path: '/party/:code/tv', element: <TvPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
